import { faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'components/ui/Button/Button';
import { Select } from 'ts/interfaces/Generic';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { Credit2000PaymentInfo, PrivilegesInfo } from 'ts/shared/interfaces/Generic';
import BookSecret from './BookSecret';
import LoaderMain from './LoaderMain';
import withErrorBoundary from './withErrorBoundary';
import MultiPaymentModal from './MultiPaymentModal';
import { formatPriceCurrency, getCurrency } from 'utils/utils';
import { refreshTokenSingleFlight } from 'services/cognito';
import { convertCurrency } from 'utils/shared/utils';
import { MainContext } from 'contexts/MainContext';
import { b2b, displaySecretBook } from 'constants/generic';

interface BookPaymentProps {
  onClickSend: () => void;
  onClickRevolut?: (currency: string, revolut2?: boolean) => void;
  onClickSubmit: (info: Credit2000PaymentInfo) => void;
  onClickMultiSubmit?: (infos: Credit2000PaymentInfo[]) => void;
  disabled: boolean;
  privileges: PrivilegesInfo;
  credit2000?: Credit2000PaymentInfo;
  multiCredit2000PaymentInfo?: Credit2000PaymentInfo[];
  isFormValid?: boolean;
  systemProducts?:
  | 'Dynamic_FMHM'
  | 'Dynamic'
  | 'Hotels'
  | 'Flights'
  | 'Transfers'
  | 'Nofshon'
  | 'Tours';
  total?: {
    value: number;
    currency: string;
  };
  saleValue?: {
    price: number;
    currency: string;
  }

}

const BookPayment: FC<BookPaymentProps> = (props) => {
  const [t] = useTranslation();
  const [normalList, setNormalList] = useState<Select[]>([]);
  const [credit2000model, setCredit2000Model] = useState<Credit2000PaymentInfo | undefined>(
    props?.credit2000
  );
  const [showPasswordModal, setShowPasswordModal] = useState(0);
  const [showMultiPaymentModal, setShowMultiPaymentModal] = useState(false);
  const { initSiteData } = useContext(MainContext);
  const [cardsCount, setCardsCount] = useState(1);

  const [revolutSecondPrice, setRevolutSecondPrice] = useState(0);
  const [revolutPrice, setRevolutPrice] = useState(0);

  useEffect(() => {
    (async () => {
      const basePrice2 = props.privileges?.revolut?.priceSecond || 0;
      const basePrice = props.privileges?.revolut?.price || 0;

      if (typeof props.saleValue?.price === 'number' && props.privileges?.revolut?.price) {
        if (props.privileges?.revolut?.currency) {
          const convertedSale = await convertCurrency(
            props.saleValue.price,
            props.saleValue.currency,
            props.privileges.revolut.currency,
          );

          setRevolutPrice(basePrice + (convertedSale || 0));
        }
        if (props.privileges?.revolut?.currencySecond) {
          const convertedSale2 = await convertCurrency(
            props.saleValue.price,
            props.saleValue.currency,
            props.privileges.revolut.currencySecond,
          );

          setRevolutSecondPrice(basePrice2 + (convertedSale2 || 0));
        }
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.saleValue?.price]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getOptions = () => {
    return Array.from({ length: (initSiteData as any)?.paymentSystemMaxPayments || 0 }, (_, index) => ({
      value: index + 1,
      label: (index + 1).toString(),
    }));
  };

  useEffect(() => {
    const newNormalList =
      props.credit2000?.listOfRegularPayments
        .filter((payment) => !payment.disabled)
        .map((payment) => ({
          value: +payment.value,
          label: payment.text,
        })) || [];

    setNormalList(newNormalList);
  }, [props.credit2000]);

  const onSuccessPassword = async () => {
    if (showPasswordModal === 1) {
      await refreshTokenSingleFlight();
      props.onClickSubmit(credit2000model!);
    } else if (showPasswordModal === 2) {
      await refreshTokenSingleFlight();
      props.onClickSend();
    }

    setShowPasswordModal(0);
  };

  const onClickBook = async () => {
    if (props.isFormValid && displaySecretBook) {
      setShowPasswordModal(1);
    } else {
      await refreshTokenSingleFlight();
      props.onClickSubmit(credit2000model!);
    }
  };

  const onClickSend = async () => {
    if (props.isFormValid && displaySecretBook) {
      setShowPasswordModal(2);
    } else {
      await refreshTokenSingleFlight();
      props.onClickSend();
    }
  };

  const onBookMulti = async (credit2000PaymentInfos: Credit2000PaymentInfo[]) => {
    await refreshTokenSingleFlight();
    props.onClickMultiSubmit?.(credit2000PaymentInfos);
    setShowMultiPaymentModal(false);
  };

  const onClickBookMulti = async () => {
    setShowMultiPaymentModal(true);
  };

  const onChangeCardsCount = (value: number) => {
    setCardsCount(value);
  };

  const creditCardAllowed = props.privileges?.creditCardAllowed && !!props.credit2000 && (initSiteData?.other?.allowCreditCard === 'true' || !b2b);
  const revolutAllowed = props.privileges?.revolut?.price && props.privileges?.revolut?.currency;
  const propodalAllowed = props.privileges?.bookAllowed && b2b;

  if (!creditCardAllowed && !revolutAllowed && !propodalAllowed) return null;

  return (
    <Fragment>
      <div className="title text-md mb-15">{t('passenger.methods_of_payment')}</div>
      <div className="book-payment mb-20">
        {creditCardAllowed && !revolutAllowed && (
          <Fragment>
            <div className="title text-md mb-20 mt-10">
              <FontAwesomeIcon icon={faCreditCard} className="mr-5 text-sm text-contrast" />
              <span className="mr-5">{t('passenger.fast_secure')}</span>
            </div>
            <div className="row">
              <div className="col-md-9 col-12">
                {credit2000model?.numberOfRegularPayments && (
                  <div className="form-group">
                    <div className="mb-5">{t('passenger.number_of_payments')}</div>
                    <SelectCustom
                      options={normalList}
                      onChange={(numberOfRegularPayments) =>
                        setCredit2000Model({ ...credit2000model, numberOfRegularPayments })
                      }
                      value={credit2000model.numberOfRegularPayments}
                      appendToBody={true}
                    />
                  </div>
                )}
              </div>
              <div className="col-md-3 col-12 book-payment-topbtn">
                <Button
                  className="btn btn-primary btn-lg btn-green"
                  title={t('hotel.book')}
                  onClick={() => onClickBook()}
                  disabled={props.disabled}
                />
              </div>
            </div>
          </Fragment>
        )}

        {revolutAllowed && (
          <div>
            <div className="form-wrapper row">
              <div className="col-12 col-md-6">
                <div className="title text-md">
                  <FontAwesomeIcon icon={faCreditCard} className="mr-5 text-sm text-contrast" />
                  <span>
                    {t('passenger.procces_with_revolut')}{' '}
                    {props.privileges?.revolut2 && <span>{t('passenger.as_client')}</span>}
                    <span>

                    </span>
                  </span>
                </div>
              </div>
              <div className="col-md-3 col-12 mb-10">
                {
                  props.privileges?.revolut?.currencySecond && props.privileges?.revolut?.priceSecond && (<Button
                    className="btn btn-primary btn-lg btn-green"
                    title={t('passenger.pay') + '  ' + formatPriceCurrency(
                      revolutSecondPrice,
                      getCurrency(props.privileges?.revolut?.currencySecond || ''),
                      true
                    )}
                    onClick={() => props.onClickRevolut?.(props.privileges?.revolut?.currencySecond || '')}
                    disabled={props.disabled}
                  />)
                }
              </div>
              <div className="col-md-3 col-12">
                <Button
                  className="btn btn-primary btn-lg btn-green"
                  title={t('passenger.pay') + ' ' + formatPriceCurrency(
                    revolutPrice,
                    getCurrency(props.privileges?.revolut?.currency || ''),
                    true
                  )}
                  onClick={() => props.onClickRevolut?.(props.privileges?.revolut?.currency || '')}
                  disabled={props.disabled}
                />
              </div>

            </div>

            {!!props.privileges?.revolut2 &&
              <div className="form-wrapper row">
                <div className='col-12'>
                  <div className='divider mb-10'></div>
                </div>

                <div className="col-12 col-md-6">
                  <div className="title text-md">
                    <span>
                      {<span>{t('passenger.as_agent')}</span>}
                      <span>
                      </span>
                    </span>
                  </div>

                </div>
                <div className="col-md-3 col-12 mb-10">
                  {
                    props.privileges?.revolut?.currencySecond && props.privileges?.revolut2?.priceSecond && (
                      <Button
                        className="btn btn-primary btn-lg"
                        title={t('passenger.pay')}
                        onClick={() => props.onClickRevolut?.(props.privileges?.revolut?.currencySecond || '', true)}
                        disabled={props.disabled}
                      />)
                  }
                </div>
                <div className="col-md-3 col-12">
                  <Button
                    className="btn btn-primary btn-lg"
                    title={t('passenger.pay')}
                    onClick={() => props.onClickRevolut?.(props.privileges?.revolut?.currency || '', true)}
                    disabled={props.disabled}
                  />
                </div>

              </div>
            }
          </div>
        )}

        {propodalAllowed && (
          <div className={'row' + (creditCardAllowed || revolutAllowed ? ' flex-between' : '')}>
            <div className="col-12 col-md-9"></div>

            <div className="col-md-3 col-12">
              <Button
                className="btn btn-primary btn-lg"
                title={t('passenger.confirm_order')}
                onClick={() => onClickSend()}
                disabled={props.disabled}
              />
            </div>
          </div>
        )}

        {(initSiteData as any)?.isMultipayment && (
          <div
            className={
              'form-wrapper row' + (creditCardAllowed || propodalAllowed || revolutAllowed ? ' flex-between' : '')
            }
          >
            <div className="col-12 col-md-6 mb-10 mt-10">
              <div className="title text-md">
                <FontAwesomeIcon icon={faCreditCard} className="mr-5 text-sm text-contrast" />
                <span>
                  {t('passenger.fast_secure_multi_payment', {
                    count: (initSiteData as any)?.paymentSystemMaxPayments,
                  })}
                </span>
              </div>
            </div>
            <div className="col-md-3 col-12 mb-10 mt-10">
              <SelectCustom
                options={getOptions()}
                value={cardsCount}
                appendToBody={true}
                onChange={(value) => onChangeCardsCount(value)}
                movablePlaceholder={t('hotel.choose_up_to_5_cards')}
                movablePlaceholderSm
              />
            </div>
            <div className="col-md-3 col-12 mb-10 mt-10">
              <Button
                className="btn btn-green btn-lg"
                title={t('hotel.book')}
                onClick={onClickBookMulti}
                disabled={props.disabled}
              />
            </div>
          </div>
        )}
      </div>
      {!!showPasswordModal && (
        <BookSecret onClose={() => setShowPasswordModal(0)} onSuccess={onSuccessPassword} />
      )}
      {props.disabled && (
        <LoaderMain
          systemProducts={props.systemProducts || 'Hotels'}
          systemProductsDetails="LoadingBook"
        />
      )}
      {showMultiPaymentModal && props.total && (
        <MultiPaymentModal
          cardsCount={cardsCount}
          onSubmit={onBookMulti}
          onClose={() => setShowMultiPaymentModal(false)}
          total={props.total}
        />
      )}
    </Fragment>
  );
};

export default withErrorBoundary(BookPayment, 'BookPayment');
